import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api";
import { LoadingState, ErrorState } from "./Loading";
import { Modal } from "./Modal";
import { CoachPlan, money } from "./PricingSettings";
import { PlanRequestSummary } from "../lib/queries";

interface PlansResponse {
  plans: CoachPlan[];
  siblingDiscountPercent: number;
}

export interface FamilyAthlete {
  client_id: string;
  name: string;
  plan_id: string | null;
  plan_name: string | null;
  plan_price_cents: number | null;
  pending_plan_id: string | null;
  pending_plan_name: string | null;
  pending_effective_at: string | null;
  stopped?: boolean;
  stopping_at?: string | null;
  stopped_by?: "coach" | "family" | null;
  /** Where the coach lets this family move. Absent on an older response, which reads as every plan. */
  offered_plan_ids?: string[];
  plan_request?: PlanRequestSummary | null;
}

const PLANS_KEY = ["billing", "plans"] as const;

/**
 * When the change takes effect and who pays for it, addressed to whoever is reading.
 *
 * Every line here used to be in the family's voice, including on the coach's client page where the
 * card being charged belongs to somebody else entirely (F42).
 */
function describeTiming(
  isDowngrade: boolean,
  actingAs: "family" | "coach",
  athleteName: string,
  newPriceCents?: number
): string {
  // The new monthly figure, said outright. "You'll only be charged the difference" described the
  // mechanism without ever giving a number, so the one question being asked -- what will this cost --
  // went unanswered on the screen asking it (UX review F29). The exact prorated amount is Stripe's to
  // compute at the moment of charge, so this states the monthly price rather than inventing one.
  const price = newPriceCents !== undefined ? `${money(newPriceCents)}/mo` : "the new price";
  if (isDowngrade) {
    return actingAs === "coach"
      ? `They keep what they have paid for until the end of this month, then ${price} starts.`
      : `You keep what you're paying for until the end of this month, then ${price} starts.`;
  }
  return actingAs === "coach"
    ? `Starts straight away at ${price}, charging ${athleteName}'s family the difference for the rest of this month.`
    : `Starts straight away at ${price} — you'll only be charged the difference for the rest of this month.`;
}



function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

/**
 * Choosing which of the coach's plans an athlete is on (finding #15).
 *
 * The family's own control, per athlete: one skater moving to a cheaper plan should not move their
 * sibling. The coach can do the same thing from the client page -- same endpoint, same rules.
 *
 * The asymmetry between up and down is stated on the buttons before either is pressed, because it is
 * the thing people are surprised by: an upgrade starts now and is charged pro-rata, a downgrade waits
 * until the end of the month they have already paid for. Neither is a technical constraint; both are
 * what is fair, and saying so is what stops it reading as a bug.
 */
export function PlanChooser({
  athlete,
  onChanged,
  actingAs = "family",
}: {
  athlete: FamilyAthlete;
  onChanged?: () => void;
  /**
   * Whose card is about to be charged, from the point of view of whoever is reading.
   *
   * The coach's client page renders this same chooser, live, and every word on it was written in the
   * family's voice -- "you'll only be charged the difference" -- while the person clicking was the
   * coach and the card belonged to somebody else (UX review F42).
   */
  actingAs?: "family" | "coach";
}) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PLANS_KEY, queryFn: () => apiGet<PlansResponse>("/billing/plans") });

  /**
   * The plan awaiting confirmation.
   *
   * An upgrade takes money from a real card the moment it is clicked, and there was nothing between the
   * click and the charge -- no confirmation, no summary, no undo. That is worth a step for a family
   * changing their own plan, and it is not optional for a coach changing somebody else's (F42).
   */
  const [confirming, setConfirming] = useState<CoachPlan | null>(null);

  const undoChange = useMutation({
    mutationFn: () => apiPost(`/billing/athletes/${athlete.client_id}/plan/cancel-change`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      onChanged?.();
    },
  });

  const choose = useMutation({
    mutationFn: (planId: string) => apiPost(`/billing/athletes/${athlete.client_id}/plan`, { planId }),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      onChanged?.();
    },
  });

  if (query.isPending) return <LoadingState label="Loading plans…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  /**
   * A family sees only what their coach offered this athlete, plus the plan they are on (27 Sep debug
   * notes item 12). The coach sees everything: offering is about what a family may pick, not what the
   * coach may do.
   */
  const offered = athlete.offered_plan_ids;
  const plans =
    actingAs === "family" && offered
      ? query.data.plans.filter((plan) => plan.id === athlete.plan_id || offered.includes(plan.id))
      : query.data.plans;
  if (plans.length === 0) {
    return <p className="empty-state">Your coach hasn&rsquo;t set up any plans yet.</p>;
  }
  if (actingAs === "family" && plans.every((plan) => plan.id === athlete.plan_id)) {
    return (
      <p className="empty-state">
        Your coach hasn&rsquo;t offered any other plans for {athlete.name}. Talk to your coach about switching.
      </p>
    );
  }
  /** Asking rather than switching: a family, and a plan the coach approves moves onto. */
  const needsAsking = (plan: CoachPlan) => actingAs === "family" && Boolean(plan.requiresApproval);
  // Only the family is shown "Requested" in place of a button: the coach moving them there directly is
  // itself the approval, and the server records it as one.
  const requestedPlanId =
    actingAs === "family" && athlete.plan_request?.status === "pending" ? athlete.plan_request.plan_id : null;

  const currentPrice = athlete.plan_price_cents;

  return (
    <div className="plan-catalogue-choice">
      {choose.isError && <ErrorState error={choose.error} />}

      {/* What they have already chosen for next month, and when it starts -- so a queued change is
          visible rather than a surprise on the next invoice. */}
      {/* A queued change, and the way out of it.
          Choosing a cheaper plan queued it and showed this badge, and that was the end of the matter --
          no control anywhere to keep the plan you were on, so a mis-tap could only be corrected by
          waiting for it to land and changing back, which for an upgrade means paying twice
          (UX review F32). */}
      {athlete.pending_plan_name && athlete.pending_effective_at && (
        <div className="confirm-box" role="group" aria-label="Scheduled plan change">
          <p>
            Moving to <strong>{athlete.pending_plan_name}</strong> on{" "}
            {formatDate(athlete.pending_effective_at)}.
          </p>
          {undoChange.isError && <ErrorState error={undoChange.error} />}
          <button type="button" className="chip" disabled={undoChange.isPending} onClick={() => undoChange.mutate()}>
            {undoChange.isPending
              ? "Cancelling…"
              : `Keep ${athlete.plan_name ?? "the current plan"} instead`}
          </button>
        </div>
      )}

      <ul className="plan-catalogue">
        {plans.map((plan) => {
          const isCurrent = plan.id === athlete.plan_id;
          const isDowngrade = currentPrice !== null && plan.priceCents < currentPrice;
          return (
            <li key={plan.id} className={isCurrent ? "plan-tile current" : "plan-tile"}>
              <div className="plan-tile-head">
                <span className="client-name">{plan.name}</span>
                {isCurrent && <span className="badge badge-ok">Current plan</span>}
              </div>
              {/* The price as the tile's headline rather than a chip in its corner: it is the number the
                  whole choice turns on. */}
              <p className="plan-tile-price">
                {money(plan.priceCents)}
                <span>/mo</span>
              </p>
              <p className="plan-description">{plan.description}</p>

              {/* The action and what it will do, together at the foot of the tile -- so on a wide screen,
                  where the tiles sit side by side, every tile's button lines up with the next one's. */}
              {!isCurrent && (
                <div className="plan-tile-action">
                  {plan.id === requestedPlanId ? (
                    <span className="badge badge-warn">Requested</span>
                  ) : (
                    <button
                      type="button"
                      className="chip"
                      disabled={choose.isPending}
                      onClick={() => setConfirming(plan)}
                    >
                      {needsAsking(plan) ? "Ask your coach" : isDowngrade ? "Switch at the end of the month" : "Switch now"}
                    </button>
                  )}
                  <p className="plan-tile-timing">
                    {needsAsking(plan)
                      ? "Your coach approves switches to this plan — we'll ask them for you and let you know."
                      : describeTiming(isDowngrade, actingAs, athlete.name, plan.priceCents)}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {confirming && needsAsking(confirming) && (
        <Modal title="Ask to switch?" onClose={() => setConfirming(null)}>
          <p className="form-hint">
            We&rsquo;ll ask your coach to move {athlete.name} from <strong>{athlete.plan_name ?? "their plan"}</strong> to{" "}
            <strong>{confirming.name}</strong>, at <strong>{money(confirming.priceCents)}/mo</strong>. Nothing changes,
            and nothing is charged, until they say yes.
          </p>
          {choose.isError && <ErrorState error={choose.error} />}
          <button type="button" disabled={choose.isPending} onClick={() => choose.mutate(confirming.id)}>
            {choose.isPending ? "Sending…" : "Send request"}
          </button>
          <button type="button" className="chip" onClick={() => setConfirming(null)}>
            Never mind
          </button>
        </Modal>
      )}

      {confirming && !needsAsking(confirming) && (
        <Modal title="Change this plan?" onClose={() => setConfirming(null)}>
          <p className="form-hint">
            {actingAs === "coach" ? `${athlete.name} moves` : "You move"} from{" "}
            <strong>{athlete.plan_name ?? "no plan"}</strong> to <strong>{confirming.name}</strong>, at{" "}
            <strong>{money(confirming.priceCents)}/mo</strong>.
          </p>
          <p className="form-hint">
            {describeTiming(
              currentPrice !== null && confirming.priceCents < currentPrice,
              actingAs,
              athlete.name,
              confirming.priceCents
            )}
          </p>
          {choose.isError && <ErrorState error={choose.error} />}
          <button type="button" disabled={choose.isPending} onClick={() => choose.mutate(confirming.id)}>
            {choose.isPending ? "Changing…" : "Yes, change the plan"}
          </button>
          <button type="button" className="chip" onClick={() => setConfirming(null)}>
            Keep {athlete.plan_name ?? "things as they are"}
          </button>
        </Modal>
      )}
    </div>
  );
}
